import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";

const EMPTY_JOB = {
  title_el: "", title_en: "",
  location: "Αθήνα, Ελλάδα",
  employment_type: "full_time",
  department: "",
  description_el: "", description_en: "",
  requirements_el: "", requirements_en: "",
  salary_range: "",
  apply_email: "careers@amazypet.com",
  active: true,
};

const AdminJobs = () => {
  const { t } = useLang();
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_JOB);

  const load = () => api.get("/jobs?include_inactive=true").then((r) => setJobs(r.data || []));

  useEffect(() => {
    load();
  }, []);

  const open = (j) => {
    setEditing(j?.id || "new");
    setForm(j ? { ...EMPTY_JOB, ...j } : EMPTY_JOB);
  };
  const close = () => { setEditing(null); setForm(EMPTY_JOB); };

  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const save = async () => {
    try {
      const payload = { ...form };
      if (editing === "new") {
        await api.post("/jobs", payload);
        toast.success("Position created");
      } else {
        await api.patch(`/jobs/${editing}`, payload);
        toast.success("Updated");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => open(null)} className="luxury-btn" data-testid="add-job-btn">
          <Plus size={16} /> {t("admin.addJob")}
        </button>
      </div>
      <div className="bg-white border border-[#EBE5DF] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F2ED]">
            <tr>
              <th className="text-left px-4 py-3">{t("admin.jobTitle")}</th>
              <th className="text-left px-4 py-3">{t("admin.location")}</th>
              <th className="text-left px-4 py-3">{t("admin.employmentType")}</th>
              <th className="text-left px-4 py-3">{t("admin.department")}</th>
              <th className="text-left px-4 py-3">{t("admin.active")}</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} className="border-t border-[#EBE5DF]" data-testid={`job-row-${j.id}`}>
                <td className="px-4 py-3">{j.title_el}</td>
                <td className="px-4 py-3 text-[#736B66]">{j.location}</td>
                <td className="px-4 py-3 text-[#736B66]">{t(`careers.employment.${j.employment_type}`)}</td>
                <td className="px-4 py-3 text-[#736B66]">{j.department || "—"}</td>
                <td className="px-4 py-3">{j.active ? "✓" : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => open(j)} className="text-[#2C1E16] hover:text-[#D4AF37] mr-3" data-testid={`edit-job-${j.id}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(j.id)} className="text-[#736B66] hover:text-[#8B0000]" data-testid={`delete-job-${j.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {jobs.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-[#736B66]">No jobs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="job-form">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">{editing === "new" ? t("admin.addJob") : t("admin.editJob")}</h2>
              <button onClick={close}><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="overline mb-1 block">{t("admin.jobTitle")} (GR)</label>
                <input className="luxury-input-box" value={form.title_el} onChange={onChange("title_el")} data-testid="job-title-el" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.jobTitle")} (EN)</label>
                <input className="luxury-input-box" value={form.title_en} onChange={onChange("title_en")} data-testid="job-title-en" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.location")}</label>
                <input className="luxury-input-box" value={form.location} onChange={onChange("location")} />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.employmentType")}</label>
                <select className="luxury-input-box" value={form.employment_type} onChange={onChange("employment_type")}>
                  <option value="full_time">Πλήρης απασχόληση</option>
                  <option value="part_time">Μερική απασχόληση</option>
                  <option value="internship">Πρακτική</option>
                  <option value="freelance">Freelance</option>
                </select>
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.department")}</label>
                <input className="luxury-input-box" value={form.department || ""} onChange={onChange("department")} />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.salaryRange")}</label>
                <input className="luxury-input-box" value={form.salary_range || ""} onChange={onChange("salary_range")} />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-1 block">{t("admin.applyEmail")}</label>
                <input className="luxury-input-box" value={form.apply_email || ""} onChange={onChange("apply_email")} />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-1 block">{t("admin.descEl")}</label>
                <textarea rows={4} className="luxury-input-box" value={form.description_el} onChange={onChange("description_el")} />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-1 block">{t("admin.descEn")}</label>
                <textarea rows={4} className="luxury-input-box" value={form.description_en} onChange={onChange("description_en")} />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-1 block">{t("admin.requirementsEl")}</label>
                <textarea rows={3} className="luxury-input-box" value={form.requirements_el || ""} onChange={onChange("requirements_el")} />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-1 block">{t("admin.requirementsEn")}</label>
                <textarea rows={3} className="luxury-input-box" value={form.requirements_en || ""} onChange={onChange("requirements_en")} />
              </div>
              <div className="sm:col-span-2 flex items-center gap-2">
                <input type="checkbox" id="job-active" checked={!!form.active} onChange={onChange("active")} />
                <label htmlFor="job-active" className="text-sm">{t("admin.active")}</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={close} className="luxury-btn-outline">{t("admin.cancel")}</button>
              <button onClick={save} className="luxury-btn" data-testid="save-job">{t("admin.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJobs;
